import { useId } from 'react';
import { cn } from '@/lib/cn';

type ToggleAccent = 'primary' | 'income' | 'expense' | 'cards' | 'balance';

const ACCENT_ON: Record<ToggleAccent, string> = {
  primary: 'bg-primary',
  income: 'bg-income',
  expense: 'bg-expense',
  cards: 'bg-cards',
  balance: 'bg-balance',
};

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Cor do trilho quando ligado. */
  accent?: ToggleAccent;
  label?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}

/** Switch acessível (role switch). O thumb desliza; a cor "ligado" vem de `accent`. */
export function Toggle({
  checked,
  onChange,
  accent = 'primary',
  label,
  disabled = false,
  id,
  className,
}: ToggleProps) {
  const autoId = useId();
  const switchId = id ?? autoId;

  const button = (
    <button
      type="button"
      role="switch"
      id={switchId}
      aria-checked={checked}
      aria-label={label ? undefined : 'Alternar'}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-grid size-11 shrink-0 place-items-center rounded-full',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute h-6 w-11 rounded-full transition-colors',
          checked ? ACCENT_ON[accent] : 'bg-border',
        )}
      />
      <span
        className={cn(
          'absolute left-0.5 size-5 rounded-full bg-white shadow-sm transition-transform',
          checked ? 'translate-x-[22px]' : 'translate-x-0',
        )}
      />
    </button>
  );

  if (!label) return button;

  return (
    <label htmlFor={switchId} className={cn('flex items-center justify-between gap-3', className)}>
      <span className="text-[15px] text-ink">{label}</span>
      {button}
    </label>
  );
}
